import PropTypes from 'prop-types'
import React, { Component } from 'react'
import { get } from 'lodash'
import { FormattedMessage } from 'react-intl'
import { compose } from 'recompose'
import { Input, RadioGroup, Radio } from 'components/fields'
import { CONFIGURATION_KEYS, LEVELS, PAYMENT_TYPES } from 'constants/enums'
import { wizard, meta } from 'helpers/decorators'
import { apiAwards, apiConfiguration, apiParticipants } from 'helpers/decorators/api'
import { Title, Button, FormDebug } from 'components/misc'
import validate from './level.validation'
import { participants } from 'constants/wizards'
import { connect } from 'react-redux'
import { initializeFormFields } from 'helpers/formInitializationHelper'
import { getPaymentTypeId } from 'helpers/getPaymentTypeId'
import { allowedPaymentKeys, skippablePaymentTypes } from 'constants/wizards/participant'

const enhance = compose(
  apiAwards({
    publicLevels: true
  }),
  apiParticipants(),
  apiConfiguration(),
  wizard({
    form: 'participantRegistration',
    multipage: true,
    destroyOnUnmount: false,
    validate
  }),
  meta({
    title: 'Register participant',
    intlKey: 'pageTitle.register_participant'
  }),
  connect(
    ({ api }) => ({
      registrationInfo: get(api, 'participants.registrationInfo')
    }), {}
  )
)

class Level extends Component {
  static propTypes = {
    awards: PropTypes.object,
    errors: PropTypes.object,
    values: PropTypes.object,
    change: PropTypes.func.isRequired,
    handleSubmit: PropTypes.func.isRequired,
    getRegistrationOptions: PropTypes.func,
    changeWizardActive: PropTypes.func,
    onSubmit: PropTypes.func,
    wizardSteps: PropTypes.array,
    changeWizardNext: PropTypes.func,
    participants: PropTypes.object,
    initialize: PropTypes.func.isRequired,
    registrationInfo: PropTypes.object,
    getConfigurationKeyValue: PropTypes.func.isRequired
  }

  componentDidMount = async() => {
    const registrationInfo = get(this.props, 'registrationInfo')

    if (registrationInfo) {
      const previousCompletedAward = get(registrationInfo, 'previousCompletedAwardLevel', null) !== null
        ? 'yes'
        : 'no'
      const fieldsToInit = [
        { name: 'awardLevel.value', value: get(registrationInfo, 'awardLevel.value', null) },
        { name: 'previousCompletedAwardLevel.value', value: get(registrationInfo, 'previousCompletedAwardLevel.value', null) },
        { name: 'previousCompletedAwardPlace', value: get(registrationInfo, 'previousCompletedAwardPlace', null) },
        // Helpers
        { name: 'helpers.previousCompletedAward', value: previousCompletedAward },
      ]

      initializeFormFields(this.props, fieldsToInit)
    }
  }

  getFilteredAwards = () => {
    const { awards, values } = this.props
    const awardLevel = get(values, 'awardLevel.value')
    const filteredLevels = []

    for (const level of awards.levels) {
      if (level.value === awardLevel) {
        break
      }
      filteredLevels.push(level)
    }
    return filteredLevels
  }

  getAllowedMethodsCount = data =>
    allowedPaymentKeys.reduce(
      (total, key) => (data[key] === true ? total + 1 : total),
      0
    )

  getSkipPayment = awardLvl => {
    switch (awardLvl) {
      case LEVELS.BRONZE.id:
        return get(this.props, `configuration.values.${CONFIGURATION_KEYS.participantRegistrationSkipPayment.id}.booleanValue`)
      case LEVELS.SILVER.id:
        return get(this.props, `configuration.values.${CONFIGURATION_KEYS.participantRegistrationSkipPaymentSilver.id}.booleanValue`)
      case LEVELS.GOLD.id:
        return get(this.props, `configuration.values.${CONFIGURATION_KEYS.participantRegistrationSkipPaymentGold.id}.booleanValue`)
      default:
        return get(this.props, `configuration.values.${CONFIGURATION_KEYS.participantRegistrationSkipPayment.id}.booleanValue`)
    }
  }

  handleRadioChange = () => {
    this.props.change('previousCompletedAwardLevelId', '')
    this.props.change('previousCompletedAwardPlace', '')
  }

  handleSubmit = async fields => {
    const { rootPath, steps } = participants
    const { body } = await this.props.getRegistrationOptions({
      organizationId: get(fields, 'organizationId'),
      awardLevel: get(fields, 'awardLevel.value'),
      registrationId: get(fields, 'id', undefined),
      previousAwardLevel: get(fields, 'previousCompletedAwardLevel.value', null)
    })
    const paymentOpts = get(body, 'paymentOptions', {})
    const baseAmount = paymentOpts.baseAmount
    const allowedMethodsCount = this.getAllowedMethodsCount(paymentOpts)
    const paymentTypes = allowedPaymentKeys.filter(
      key => paymentOpts[key] === true
    )

    this.props.change(
      'helpers.awardLevelConfigurations',
      get(body, 'awardLevelConfigurations', {})
    )

    // We need to see the page for onlineManualPaymentEnabled bc it has an associated link and input
    if (
      allowedMethodsCount > 0
    ) {
      const selectedAwardLvl = get(this.props.values, 'awardLevel.value')

      let skipPayment = this.getSkipPayment(selectedAwardLvl)

      if (
        paymentTypes.some(item => skippablePaymentTypes.includes(getPaymentTypeId(item)))
        && parseFloat(baseAmount) === 0
      ) {
        skipPayment = true
      }

      this.props.changeWizardActive(steps.payment.id, !skipPayment)
    /* TODO: delete else branch, since it's not reachable */
    } else {
      // works like else for below conditions
      let stepId = steps.details.id
      const paymentType = allowedPaymentKeys.find(
        key => paymentOpts[key] === true
      )

      if (this.isStepActive(steps.profile.id)) {
        stepId = steps.profile.id
        // profile - false, parent - true
      } else if (this.isStepActive(steps.parent.id)) {
        stepId = steps.parent.id
      }

      const paymentTypeId = this.getPaymentTypeId(paymentType)
      const actualAmount =
        // eslint-disable-next-line no-nested-ternary
        [PAYMENT_TYPES.offline.id, PAYMENT_TYPES.onlineManual.id].includes(paymentTypeId)
          ? baseAmount
          : paymentTypeId === PAYMENT_TYPES.online.id
            ? paymentOpts.amountMPG
            : 0

      this.props.change('helpers.price', paymentTypeId)
      this.props.change('paymentType.value', paymentTypeId)
      this.props.changeWizardNext(stepId, rootPath + steps.account.path)
      this.props.change('awardFeeAmount', actualAmount)
      this.props.change('awardFeeCurrency', get(paymentOpts, 'currency.value'))
    }

    return this.props.onSubmit(fields)
  }

  render() {
    const { handleSubmit, values, awards, participants } = this.props
    const filteredAwards = this.getFilteredAwards()

    return (
      <form
        onSubmit={handleSubmit(this.handleSubmit)}
        data-testId="registrationAwardLevelForm"
      >
        <Title h1>
          <FormattedMessage
            id="moduleTitle.award_level"
            defaultMessage="Award Level"
          />
        </Title>

        <div className="row">
          <div className="col-xs-6">
            {awards.levels &&
              awards.levels[0] &&
              <RadioGroup
                name="awardLevel.value"
                label="What level of the Award are you registering for?"
                intlKey="label.what_level_of_the_award"
                errors={this.props.errors}
                horizontal
                dataTestId="awardLevelRadioGroup"
              >
                {awards.levels.map(item => (
                  <Radio
                    label={item.label}
                    name="awardLevel.value"
                    value={item.value}
                    key={item.value}
                  />
                ))}
              </RadioGroup>}

            {get(values, 'awardLevel.value') &&
              get(values, 'awardLevel.value') !== LEVELS.BRONZE.id &&
              <div>
                <RadioGroup
                  label="Do you have any other level completed?"
                  intlKey="label.do_you_have_any_other_level"
                  name="helpers.previousCompletedAward"
                  errors={this.props.errors}
                  horizontal
                  dataTestId="hasPreviousAwardLevelRadioGroup"
                >
                  <Radio
                    label="Yes"
                    intlKey="label.yes"
                    name="helpers.previousCompletedAward"
                    value="yes"
                  />
                  <Radio
                    label="No"
                    intlKey="label.no"
                    name="helpers.previousCompletedAward"
                    value="no"
                    onChange={this.handleRadioChange}
                  />
                </RadioGroup>
                {get(values, 'helpers.previousCompletedAward') === 'yes' &&
                  <div>
                    {filteredAwards[0] &&
                      <RadioGroup
                        name="previousCompletedAwardLevel.value"
                        label="Level"
                        intlKey="label.level"
                        errors={this.props.errors}
                        horizontal
                        dataTestId="possiblePreviousAwardLevels"
                      >
                        {filteredAwards.map(item => (
                          <Radio
                            label={item.label}
                            name="previousCompletedAwardLevel.value"
                            value={item.value}
                            key={item.value}
                          />
                        ))}
                      </RadioGroup>}

                    <Input
                      name="previousCompletedAwardPlace"
                      label="Where have you completed?"
                      intlKey="label.where_have_you_completed"
                      dataTestId="previousCompletedAwardPlace"
                    />
                  </div>}
              </div>}
          </div>
        </div>
        <FormDebug {...this.props} />
        <Button isLoading={participants && participants.loadingOptions}>
          <FormattedMessage id="button.continue" defaultMessage="Continue" />
        </Button>
      </form>
    )
  }
}

export default enhance(Level)
